import React, { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { loginRequest } from './authConfig';
import { getConfig } from './services/config';
import {
  Container,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Tab,
  Tabs
} from '@mui/material';
import { Logout as LogoutIcon, Phone as PhoneIcon, Headset as HeadsetIcon, Assessment as ReportsIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RecordingsTable from './components/RecordingsTable';
import CallPop from './components/CallPop';
import BatchTicketReview from './components/BatchTicketReview';
import Reports from './components/Reports';
import { searchRecordings, getExtensions } from './services/api';

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [recordings, setRecordings] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [extensionsLoading, setExtensionsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [callPopEnabled, setCallPopEnabled] = useState(false);
  const [ticketSearchEnabled, setTicketSearchEnabled] = useState(false);
  const [reportsEnabled, setReportsEnabled] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    extension: '',
    phoneNumber: '',
    startDate: null,
    endDate: null,
    direction: '',
    ticketNumber: ''
  });

  // Handle login
  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  // Handle logout
  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // Load config, extensions, and auto-search on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadExtensions();
      // Load config to check if features are enabled
      getConfig().then(config => {
        setCallPopEnabled(config.callPopEnabled === true);
        setTicketSearchEnabled(config.ticketSearchEnabled === true);
        setReportsEnabled(config.reportsEnabled === true);
      }).catch(console.error);
      // Auto-search on page load
      handleSearch(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadExtensions = async () => {
    setExtensionsLoading(true);
    try {
      const data = await getExtensions();
      setExtensions(data.extensions || []);
    } catch (err) {
      setError('Failed to load extensions');
    } finally {
      setExtensionsLoading(false);
    }
  };

  const handleSearch = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
        endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null,
        page
      };
      const data = await searchRecordings(params);
      setRecordings(data.recordings);
      setTotalCount(data.totalCount);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError('Failed to load recordings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      extension: '',
      phoneNumber: '',
      startDate: null,
      endDate: null,
      direction: '',
      ticketNumber: ''
    });
    setRecordings([]);
    setTotalCount(0);
    setCurrentPage(1);
    setTotalPages(1);
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            3CX Recordings Vault
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please sign in with your Microsoft account to access call recordings.
          </Typography>
          <Button variant="contained" size="large" onClick={handleLogin}>
            Sign In with Microsoft
          </Button>
        </Paper>
      </Container>
    );
  }

  // Recordings search page content (inline JSX to prevent re-mounting on state change)
  const recordingsPageContent = (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Filters
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Extension</InputLabel>
            <Select
              value={filters.extension}
              label="Extension"
              onChange={(e) => handleFilterChange('extension', e.target.value)}
              disabled={extensionsLoading}
              endAdornment={
                extensionsLoading && (
                  <Box sx={{ pr: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )
              }
            >
              <MenuItem value="">All Extensions</MenuItem>
              {extensions.map((ext) => (
                <MenuItem key={ext.extension} value={ext.extension}>
                  {ext.extension} - {ext.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Direction</InputLabel>
            <Select
              value={filters.direction}
              label="Direction"
              onChange={(e) => handleFilterChange('direction', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Inbound">Inbound</MenuItem>
              <MenuItem value="Outbound">Outbound</MenuItem>
              <MenuItem value="Internal">Internal</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Phone Number"
            value={filters.phoneNumber}
            onChange={(e) => handleFilterChange('phoneNumber', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
            placeholder="Search by phone number"
          />

          {ticketSearchEnabled && (
            <TextField
              fullWidth
              label="Ticket #"
              value={filters.ticketNumber}
              onChange={(e) => handleFilterChange('ticketNumber', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
              placeholder="Search by ticket number"
            />
          )}

          <DatePicker
            label="Start Date"
            value={filters.startDate}
            onChange={(date) => handleFilterChange('startDate', date)}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <DatePicker
            label="End Date"
            value={filters.endDate}
            onChange={(date) => handleFilterChange('endDate', date)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => handleSearch(1)}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Search'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            disabled={loading}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {recordings.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Found {totalCount} recording{totalCount !== 1 ? 's' : ''}
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </Typography>
          <RecordingsTable
            recordings={recordings}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => handleSearch(page)}
          />
        </>
      )}

      {!loading && recordings.length === 0 && totalCount === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No recordings found. Try adjusting your search filters.
          </Typography>
        </Paper>
      )}
    </Container>
  );

  // Navigation component that uses useLocation for active tab highlighting
  const Navigation = () => {
    const location = useLocation();

    return (
      <AppBar position="static">
        <Toolbar sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, minHeight: { xs: 'auto', sm: 64 } }}>
          <Typography variant="h6" component="div" sx={{ mr: { xs: 1, sm: 4 }, display: { xs: 'none', sm: 'block' } }}>
            3CX Recordings Vault
          </Typography>
          <Tabs
            value={location.pathname}
            textColor="inherit"
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ flexGrow: 1 }}
          >
            <Tab
              icon={<HeadsetIcon />}
              iconPosition="start"
              label="Recordings"
              value="/"
              component={Link}
              to="/"
            />
            {callPopEnabled && (
              <Tab
                icon={<PhoneIcon />}
                iconPosition="start"
                label="Live Calls"
                value="/call-pop"
                component={Link}
                to="/call-pop"
              />
            )}
            {callPopEnabled && (
              <Tab
                icon={<AutoAwesomeIcon />}
                iconPosition="start"
                label="Batch Review"
                value="/batch-review"
                component={Link}
                to="/batch-review"
              />
            )}
            {reportsEnabled && (
              <Tab
                icon={<ReportsIcon />}
                iconPosition="start"
                label="Reports"
                value="/reports"
                component={Link}
                to="/reports"
              />
            )}
          </Tabs>
          <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>
            {accounts[0]?.name || accounts[0]?.username}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout} title="Sign out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
    );
  };

  return (
    <BrowserRouter>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Navigation />
        <Routes>
          <Route path="/" element={recordingsPageContent} />
          <Route path="/call-pop" element={<CallPop />} />
          <Route path="/batch-review" element={<BatchTicketReview />} />
          <Route path="/reports" element={<Reports />} />
        </Routes>
      </LocalizationProvider>
    </BrowserRouter>
  );
}

export default App;
